import { Button, Tag } from "antd"
import { useEffect, useMemo, useState } from "react"
import dayjs from "dayjs"
import relativeTime from "dayjs/plugin/relativeTime"
import apiClient from "../../../Utils/apiClient"
import PanelLayout from "../../../Layout/PanelLayout"
import TableBox from "../../../components/TableBox/TableBox"
import HostnameLink from "../../../components/HostnameLink/HostnameLink"
import DeviceModelImage from "../../../components/DeviceModelImage/DeviceModelImage"
import StatusTag from "../../../components/StatusTag/StatusTag"
import EmptyState from "../../../components/EmptyState/EmptyState"
import AutoCompleteBox from "../../../components/AutoCompleteBox/AutoCompleteBox"
import { getStoredPageSize } from "../../../Utils/TableUtils/TableUtils"
import { renderDate } from "../../../Utils/utils"
import { resolveDeviceModelImage } from "../../../Utils/deviceModelImage"
import "../../Operations/Operations.scss"

dayjs.extend(relativeTime)

const shortUser = (value) => {
  const text = String(value || "").trim()
  if (!text) return "Unassigned"
  const parts = text.split(/\\|\//)
  return parts[parts.length - 1] || text
}

const LeftOnDevices = () => {
  const page = "Left On Devices"
  const [filters, setFilters] = useState({ hostname: "", user: "" })
  const [minDays, setMinDays] = useState(1)
  const [statusChip, setStatusChip] = useState("")
  const [paginationData, setPaginationData] = useState({
    page: 1,
    pageSize: getStoredPageSize(page) || 10,
    total: 0,
  })
  const [loading, setLoading] = useState(true)
  const [devices, setDevices] = useState([])
  const [summary, setSummary] = useState({
    all: 0,
    online: 0,
    away: 0,
    twoPlus: 0,
    fivePlus: 0,
    reminded: 0,
  })

  const fetchData = async (
    customPage = paginationData.page,
    customPageSize = paginationData.pageSize,
    customFilters = filters,
    customMinDays = minDays,
    customStatus = statusChip
  ) => {
    setLoading(true)
    try {
      const params = {
        page: customPage,
        pageSize: customPageSize,
        minDays: customMinDays,
        hostname: customFilters.hostname || undefined,
        user: customFilters.user || undefined,
        deviceStatus: customStatus || undefined,
      }
      const res = await apiClient("devices/left-on", "GET", null, { params })
      if (res?.data) {
        setDevices(res.data)
        setPaginationData(res.pageData || { page: customPage, pageSize: customPageSize, total: 0 })
        if (res.summary) setSummary(res.summary)
      }
    } catch (err) {
      console.log("Error fetching left-on devices", err)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchData(1, paginationData.pageSize)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleTableChange = (pagination) => {
    const { current, pageSize } = pagination
    setPaginationData((prev) => ({ ...prev, page: current, pageSize }))
    fetchData(current, pageSize)
  }

  const handleClearFilter = () => {
    const empty = { hostname: "", user: "" }
    setFilters(empty)
    setStatusChip("")
    fetchData(1, paginationData.pageSize, empty, minDays, "")
  }

  const dayChips = useMemo(
    () => [
      { key: 1, label: "1+ day", value: summary.all, tone: "#d97706", soft: "rgba(245, 158, 11, 0.16)" },
      { key: 2, label: "2+ days", value: summary.twoPlus, tone: "#ea580c", soft: "rgba(234, 88, 12, 0.16)" },
      { key: 5, label: "5+ days", value: summary.fivePlus, tone: "#b91c1c", soft: "rgba(185, 28, 28, 0.14)" },
    ],
    [summary]
  )

  const statusKpis = [
    { key: "", label: "Still on", value: summary.all, tone: "#2563eb", soft: "rgba(37, 99, 235, 0.12)" },
    { key: "Online", label: "Online", value: summary.online, tone: "#059669", soft: "rgba(34, 197, 94, 0.14)" },
    { key: "Away", label: "Away", value: summary.away, tone: "#d97706", soft: "rgba(245, 158, 11, 0.16)" },
    {
      key: "__reminded",
      label: "Reminded today/prior",
      value: summary.reminded,
      tone: "#7c3aed",
      soft: "rgba(124, 58, 237, 0.12)",
      readOnly: true,
    },
  ]

  const columns = [
    {
      title: "Device",
      dataIndex: "hostname",
      key: "hostname",
      width: 280,
      render: (text, record) => {
        const model = resolveDeviceModelImage(record)
        return (
          <div className="inv-device">
            <DeviceModelImage device={record} size="sm" />
            <div className="inv-device__copy">
              <div className="inv-device__title">
                <span
                  className={`inv-device__pulse inv-device__pulse--${String(
                    record.deviceStatus || "offline"
                  )
                    .toLowerCase()
                    .replace(/\s+/g, "-")}`}
                  aria-hidden
                />
                <HostnameLink record={record}>{text}</HostnameLink>
              </div>
              <div className="inv-device__meta" title={record.user || ""}>
                <span>{shortUser(record.user)}</span>
                <span className="inv-device__dot" />
                <span title={model.displayModel}>{model.label}</span>
              </div>
            </div>
          </div>
        )
      },
    },
    {
      title: "Status",
      dataIndex: "deviceStatus",
      width: 110,
      render: (status) => <StatusTag status={status} />,
    },
    {
      title: "Uptime",
      dataIndex: "uptimeDays",
      key: "uptimeDays",
      width: 150,
      render: (days, record) => {
        const n = Number(days) || 0
        const color = n >= 5 ? "error" : n >= 2 ? "warning" : "processing"
        return (
          <div className="reboot-when">
            <Tag color={color}>
              {n >= 1 ? `${n}d` : "<1d"} · {record.nightsOn || 0} night
              {(record.nightsOn || 0) === 1 ? "" : "s"}
            </Tag>
            <span className="ops-muted">
              Boot {record.reboot ? dayjs(record.reboot).fromNow() : "unknown"}
            </span>
          </div>
        )
      },
    },
    {
      title: "Last shutdown",
      dataIndex: "shutdown",
      key: "shutdown",
      width: 160,
      render: (text, record) => {
        if (!text) return <span className="ops-muted">Never recorded</span>
        return (
          <div className="reboot-when">
            <span>{renderDate(text)}</span>
            <span className="ops-muted">
              {record.daysSinceShutdown != null
                ? `${record.daysSinceShutdown}d ago`
                : dayjs(text).fromNow()}
            </span>
          </div>
        )
      },
    },
    {
      title: "Shutdown reminder",
      key: "reminder",
      width: 150,
      render: (_, record) =>
        record.lastShutdownReminderOn ? (
          <Tag color="purple">{record.lastShutdownReminderOn}</Tag>
        ) : (
          <span className="ops-muted">None</span>
        ),
    },
    {
      title: "Location",
      dataIndex: "location",
      key: "location",
      ellipsis: true,
      render: (text) => text || <span className="ops-muted">—</span>,
    },
  ]

  return (
    <PanelLayout
      title="Left on overnight"
      subtitle="Windows clients still powered on for 1+ days — people who skip daily shutdown."
    >
      <div className="ops-kpis">
        {dayChips.map((item) => (
          <button
            type="button"
            key={`days-${item.key}`}
            className={`ops-kpi${minDays === item.key ? " is-active" : ""}`}
            style={{ "--act-tone": item.tone, "--act-soft": item.soft }}
            onClick={() => {
              setMinDays(item.key)
              setPaginationData((prev) => ({ ...prev, page: 1 }))
              fetchData(1, paginationData.pageSize, filters, item.key, statusChip)
            }}
          >
            <div>
              <span className="ops-kpi-label">{item.label}</span>
              <strong>{item.value}</strong>
            </div>
          </button>
        ))}
        {statusKpis.map((item) => (
          <button
            type="button"
            key={item.key || "all-status"}
            className={`ops-kpi${
              !item.readOnly && statusChip === item.key ? " is-active" : ""
            }`}
            style={{ "--act-tone": item.tone, "--act-soft": item.soft }}
            onClick={() => {
              if (item.readOnly) return
              setStatusChip(item.key)
              setPaginationData((prev) => ({ ...prev, page: 1 }))
              fetchData(1, paginationData.pageSize, filters, minDays, item.key)
            }}
          >
            <div>
              <span className="ops-kpi-label">{item.label}</span>
              <strong>{item.value}</strong>
            </div>
          </button>
        ))}
      </div>

      <div className="ops-toolbar">
        <span className="ops-toolbar__count">
          {paginationData.total} device{paginationData.total === 1 ? "" : "s"} left on ≥{minDays}d
        </span>
        <Button
          size="small"
          onClick={() => fetchData(paginationData.page, paginationData.pageSize)}
        >
          Refresh
        </Button>
      </div>

      {devices.length || loading ? (
        <TableBox
          page={page}
          title={null}
          columns={columns}
          data={devices}
          loading={loading}
          className="dashboard-inventory-table"
          rowKey="_id"
          pagination={{
            current: paginationData.page,
            pageSize: paginationData.pageSize,
            total: paginationData.total,
            showSizeChanger: true,
          }}
          onChange={handleTableChange}
          enableFilter
          filtercontent={
            <div className="p-3">
              <AutoCompleteBox
                label="Hostname"
                name="hostname"
                className="mb-3 w-100"
                endPoint="users"
                placeholder="Search hostname"
                value={filters.hostname}
                onChange={(value) =>
                  setFilters((prev) => ({ ...prev, hostname: value }))
                }
              />
              <AutoCompleteBox
                label="User"
                name="user"
                className="mb-3 w-100"
                endPoint="users"
                placeholder="Search user"
                value={filters.user}
                onChange={(value) =>
                  setFilters((prev) => ({ ...prev, user: value }))
                }
              />
            </div>
          }
          onFilter={() => {
            setPaginationData((prev) => ({ ...prev, page: 1 }))
            fetchData(1, paginationData.pageSize, filters, minDays, statusChip)
          }}
          onClear={handleClearFilter}
          filters={filters}
        />
      ) : (
        <EmptyState
          title="Nobody left on"
          description={`No Windows clients are still powered on for ${minDays}+ day${
            minDays === 1 ? "" : "s"
          }.`}
        />
      )}
    </PanelLayout>
  )
}

export default LeftOnDevices
